import * as Sentry from "@sentry/react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "i18next";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    Sentry.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
  }

  render() {
    if (this.state.hasError) {
      const t = (key: string, fallback: string) => {
        try {
          const val = i18n.t(key);
          return val && val !== key ? val : fallback;
        } catch {
          return fallback;
        }
      };

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground p-6 text-center">
          <div className="w-full max-w-sm mx-auto rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl p-8 flex flex-col items-center gap-6">
            <img
              src="/logo.png"
              alt="SmartPoker"
              className="h-16 w-16 rounded-xl"
            />
            <div>
              <h1 className="text-2xl font-bold mb-2">
                {t("error_title", "Algo ha salido mal")}
              </h1>
              <p className="text-muted-foreground text-sm">
                {this.state.error?.message || "An unexpected error occurred."}
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              {t("error_reload", "Recargar")}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
